import React from 'react';

/* ────────────────────────────────────────────────────────────
   CVFlow — Onboarding Motifs (handoff bundle)

   Five animated motifs as React components. Each renders a
   self-contained `.motif` frame with its own entry animation.

   ─── HOW THIS FILE IS WIRED ────────────────────────────────────
   For maximum portability, components are defined as plain `const`s
   at module scope. The very bottom of this file does ONE of two things:

     • If `window` exists (browser / preview.html / Babel-in-browser):
       attach all five components to window globals.
     • If you're building with a bundler (Vite, Next, Webpack, CRA):
       replace the bottom `window` block with a real ES export, like:

           export { Postkarte, Kompass, VorherNachher, JaStapel, Los };

       and add `import React from 'react';` at the top if your project
       uses the classic JSX transform (most modern setups don't need this).

   Usage (after copy + CSS import):

     import { Postkarte, Kompass, VorherNachher, JaStapel, Los } from './motifs/Motifs';
     import './motifs/motifs.css';

     <Postkarte logoUrl="/assets/logo.png" />
     <Kompass   logoUrl="/assets/logo.png" needleAngle={33} />
     <VorherNachher />
     <JaStapel />
     <Los />

   Each component renders `<div className="motif motif--{name} motif--bg-{teal|cream}">`
   sized 480×720 by default. Add `fill` prop or use `.motif--fill` class for
   100%×100% fill of a sized parent. Animation replays when the component
   remounts — bump a `key` prop to retrigger on a CTA click.
   ─────────────────────────────────────────────────────────────── */


/* helper — wraps each motif with the standard frame + bg + grain */
const Frame = ({ kind, bg, fill, className = '', style, children }) => (
  <div
    className={`motif motif--${kind} motif--bg-${bg} ${fill ? 'motif--fill' : ''} ${className}`}
    style={style}
  >
    {bg === 'teal' && <div className="motif-grain" />}
    {children}
  </div>
);

/* ════════════════════════════════════════════════════════════
   1) POSTKARTE
   ════════════════════════════════════════════════════════════ */
const Postkarte = ({
  logoUrl = '/assets/logo.png',
  greeting = 'Willkommen.',
  message = 'Schön, dass du da bist. Lass uns gleich loslegen — heute machen wir deinen ersten Bewerbungs-Flow gemeinsam fertig.',
  signature = 'cvflow',
  postmarkLines = ['CVFLOW', 'Erste Etappe', 'Onboarding'],
  fill,
  style,
  className,
}) => (
  <Frame kind="postkarte" bg="teal" fill={fill} style={style} className={className}>
    <div className="p1d-stage">
      <div className="p1d-card">
        <div className="p1d-head">
          <span className="p1d-brand">CARTE POSTALE · CVFLOW</span>
        </div>
        <div className="p1d-body">
          <div className="p1d-left">
            <div className="p1d-hand">{greeting}</div>
            <div className="p1d-msg">{message}</div>
            <div className="p1d-msg-sig">{signature}</div>
          </div>
          <div className="p1d-divider" />
          <div className="p1d-right">
            <div className="p1d-address-label">An</div>
            <div className="p1d-addr">
              <span /><span /><span style={{ width: '70%' }} /><span style={{ width: '40%' }} />
            </div>
            <div className="p1d-stamp">
              <img src={logoUrl} alt="" className="p1d-stamp-logo" />
              <span className="p1d-stamp-val">Free</span>
            </div>
            <div className="p1d-mark">
              <span>{postmarkLines[0]}</span>
              <em>{postmarkLines[1]}</em>
              <span>{postmarkLines[2]}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Frame>
);

/* ════════════════════════════════════════════════════════════
   2) KOMPASS
   ════════════════════════════════════════════════════════════ */
const Kompass = ({
  logoUrl = '/assets/logo.png',
  titles = [
    'Werkstudent','Praktikum','Minijob','Ausbildung',
    'Junior-Job','Quereinstieg','Freelance','Selbstständig',
    'Vollzeit','Remote','International','Traineeship',
  ],
  needleAngle = 33,
  fill,
  style,
  className,
}) => {
  const N = titles.length;
  return (
    <Frame kind="kompass" bg="cream" fill={fill} style={style} className={className}>
      <div className="fk-stage">
        <div className="fk-compass">
          <div className="fk-ring" />
          <svg viewBox="0 0 280 280" className="fk-ticks">
            {Array.from({ length: 60 }).map((_, i) => {
              const a = (i / 60) * Math.PI * 2;
              const major = i % 15 === 0;
              const mid = i % 5 === 0;
              const r1 = major ? 110 : mid ? 118 : 122;
              const r2 = 130;
              const x1 = 140 + Math.cos(a) * r1, y1 = 140 + Math.sin(a) * r1;
              const x2 = 140 + Math.cos(a) * r2, y2 = 140 + Math.sin(a) * r2;
              return (
                <line
                  key={i}
                  x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke="#001A15"
                  strokeWidth={major ? 1.5 : mid ? 1 : 0.6}
                  opacity={major ? 0.6 : mid ? 0.35 : 0.20}
                />
              );
            })}
          </svg>
          {titles.map((t, i) => {
            const angle = (i / N) * 360 - 90;
            const rad = (angle * Math.PI) / 180;
            const cx = Math.cos(rad), cy = Math.sin(rad);
            /* Labels sit with their VISUAL CENTER on the radial axis —
               "Werkstudent" centered horizontally over 12:00, "Vollzeit"
               centered on the 8:00 axis, etc. */
            const r = 175;
            const x = 250 + cx * r;
            const y = 250 + cy * r;
            return (
              <div
                key={t}
                className="fk-title"
                style={{ left: x, top: y, transform: 'translate(-50%, -50%)' }}
              >
                {t}
              </div>
            );
          })}
          <svg
            viewBox="0 0 280 280"
            className="fk-needle"
            style={{ transform: `rotate(${needleAngle}deg)` }}
          >
            <polygon points="140,40 132,140 148,140" fill="#FFBC00" />
            <polygon points="140,240 132,140 148,140" fill="#001A15" opacity="0.4" />
            <circle cx="140" cy="140" r="9" fill="#001A15" />
            <circle cx="140" cy="140" r="3.5" fill="#FFBC00" />
          </svg>
          <div className="fk-cv">
            <img src={logoUrl} alt="" />
          </div>
        </div>
      </div>
    </Frame>
  );
};

/* ════════════════════════════════════════════════════════════
   3) VORHER / NACHHER
   ════════════════════════════════════════════════════════════ */
const VorherNachher = ({
  vorherName = 'Max M.',
  vorherRole = 'Suche neue Stelle',
  nachherName = 'Max Mustermann',
  nachherRole = 'Werkstudent · Maschinenbau',
  skills = ['Python', 'CAD', 'SQL', { label: 'English C1', ghost: true }],
  matchPercent = 98,
  fill,
  style,
  className,
}) => (
  <Frame kind="vorhernachher" bg="teal" fill={fill} style={style} className={className}>
    <div className="fvn-stage">
      <div className="fvn-pair">
        {/* VORHER — generic */}
        <div className="fvn-doc fvn-before">
          <div className="fvn-doc-label">Vorher</div>
          <div className="fvn-mock">
            <div className="fvn-name">{vorherName}</div>
            <div className="fvn-role">{vorherRole}</div>

            <div className="fvn-h">Über mich</div>
            <div className="fvn-ln" /><div className="fvn-ln m" /><div className="fvn-ln s" />

            <div className="fvn-h">Berufserfahrung</div>
            <div className="fvn-ln" /><div className="fvn-ln m" />
            <div className="fvn-ln s" /><div className="fvn-ln" />
            <div className="fvn-ln m" />

            <div className="fvn-h">Ausbildung</div>
            <div className="fvn-ln" /><div className="fvn-ln s" />

            <div className="fvn-h">Kenntnisse</div>
            <div className="fvn-ln" /><div className="fvn-ln m" />
          </div>
        </div>

        {/* ARROW */}
        <div className="fvn-arrow">
          <svg
            viewBox="0 0 60 60" width="34" height="34" fill="none"
            stroke="#FFBC00" strokeWidth="2.6"
            strokeLinecap="round" strokeLinejoin="round"
          >
            <path d="M 10 30 L 50 30" />
            <path d="M 38 18 L 52 30 L 38 42" />
          </svg>
          <span>angepasst</span>
        </div>

        {/* NACHHER — specific */}
        <div className="fvn-doc fvn-after">
          <div className="fvn-doc-label after">Nachher</div>
          <div className="fvn-mock">
            <div className="fvn-name">{nachherName}</div>
            <div className="fvn-role">{nachherRole}</div>
            <div className="fvn-bar" />

            <div className="fvn-h">Profil</div>
            <div className="fvn-ln hi" /><div className="fvn-ln m" />
            <div className="fvn-ln hi" style={{ width: '66%' }} />

            <div className="fvn-h">Erfahrung <em>· 3 relevante</em></div>
            <div className="fvn-row">
              <span className="fvn-num">2024</span>
              <div className="fvn-ln hi" />
            </div>
            <div className="fvn-row">
              <span className="fvn-num">2023</span>
              <div className="fvn-ln" />
            </div>
            <div className="fvn-row">
              <span className="fvn-num">+42%</span>
              <div className="fvn-ln hi" style={{ width: '70%' }} />
            </div>

            <div className="fvn-h">Skills <em>· match</em></div>
            <div className="fvn-chips">
              {skills.map((s, i) => {
                const obj = typeof s === 'string' ? { label: s } : s;
                return (
                  <span key={i} className={`fvn-chip ${obj.ghost ? 'ghost' : ''}`}>
                    {obj.label}
                  </span>
                );
              })}
            </div>

            <div className="fvn-fit">
              <em>{matchPercent}%</em>
              <span>Match</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Frame>
);

/* ════════════════════════════════════════════════════════════
   4) JA-STAPEL
   ════════════════════════════════════════════════════════════ */
const JaStapel = ({
  letters,
  fill,
  style,
  className,
}) => {
  const defaultLetters = [
    {
      level: 'l1',
      sender: 'cvflow · BERLIN',
      department: 'Personalabteilung',
      date: '16. Mai 2026',
      recipient: ['Max Mustermann', 'Beispielstraße 1', '10115 Berlin'],
      subject: 'Ihre Bewerbung — Werkstudent · Maschinenbau',
      salutation: 'Sehr geehrter Herr Mustermann,',
      bodyLines: 9,
      closing: 'Mit freundlichen Grüßen,',
      signature: 'Die Personalabteilung',
      stamp: 'big',
    },
    {
      level: 'l2',
      sender: 'Helios AG',
      department: 'Hamburg · DE',
      date: '9. Mai 2026',
      subject: 'Werkstudentenstelle Q3 2026',
      salutation: 'Sehr geehrter Herr Mustermann,',
      bodyLines: 6,
      stamp: 'small',
    },
    {
      level: 'l3',
      sender: 'Solaris GmbH',
      department: 'München',
      date: '12. Mai 2026',
      subject: 'Ihre Bewerbung als Werkstudent · Maschinenbau',
      salutation: 'Sehr geehrter Herr Mustermann,',
      bodyLines: 6,
      stamp: 'small',
    },
    {
      level: 'l4',
      sender: 'TechVentures GmbH',
      department: 'Berlin · DE',
      date: '3. Mai 2026',
      subject: 'Ihre Bewerbung — Junior Developer',
      salutation: 'Sehr geehrter Herr Mustermann,',
      bodyLines: 5,
      stamp: 'small',
    },
  ];
  const data = letters || defaultLetters;

  return (
    <Frame kind="jastapel" bg="cream" fill={fill} style={style} className={className}>
      <div className="fja-stage">
        {data.map((L, idx) => (
          <div key={idx} className={`fja-letter fja-${L.level}`}>
            {L.sender && (
              <div className="fja-head">
                <div className="fja-sender">
                  <span>{L.sender}</span>
                  {L.department && <em>{L.department}</em>}
                </div>
                {L.date && <div className="fja-date">{L.date}</div>}
              </div>
            )}
            {L.recipient && (
              <div className="fja-recipient">
                {L.recipient.map((line, i) => (
                  <span key={i} style={{ display: 'block' }}>{line}</span>
                ))}
              </div>
            )}
            {L.subject  && <div className="fja-subj">{L.subject}</div>}
            {L.salutation && <div className="fja-salut">{L.salutation}</div>}
            {L.bodyLines && (
              <div className="fja-body">
                {Array.from({ length: L.bodyLines }).map((_, i) => {
                  const mod = i % 4;
                  const cls = mod === 1 ? 'm' : mod === 3 ? 's' : '';
                  return <div key={i} className={`fja-line ${cls}`} />;
                })}
              </div>
            )}
            {L.closing && (
              <div className="fja-closing">
                <em>{L.closing}</em>
                <span>{L.signature}</span>
              </div>
            )}
            {L.stamp && <div className={`fja-stamp ${L.stamp}`}>Zusage</div>}
          </div>
        ))}
      </div>
    </Frame>
  );
};

/* ════════════════════════════════════════════════════════════
   5) LOS — big "Los." reveal
   ════════════════════════════════════════════════════════════ */
const Los = ({
  pillLabel = 'Profil aktiv · 16. Mai 2026',
  checks = [
    { num: '01', label: 'Status & Fach' },
    { num: '02', label: 'Karriere-Ziel' },
    { num: '03', label: 'Erwartungen' },
  ],
  fill,
  style,
  className,
}) => (
  <Frame kind="los" bg="teal" fill={fill} style={style} className={className}>
    <div className="p5d-stage">
      <div className="p5d-mini">
        <span className="dot" />
        {pillLabel}
      </div>
      <div className="p5d-word">
        Los<span className="p5d-dot">.</span>
      </div>
      <div className="p5d-arrow">
        <svg viewBox="0 0 80 24" width="80" height="24" fill="none"
             stroke="#FFBC00" strokeWidth="2.4"
             strokeLinecap="round" strokeLinejoin="round">
          <path d="M 4 12 L 70 12" />
          <path d="M 58 4 L 74 12 L 58 20" />
        </svg>
      </div>
      <div className="p5d-foot">
        {checks.map((c, i) => (
          <div key={i} className="p5d-foot-row">
            <span>{c.num}</span>
            <em>{c.label}</em>
            <span className="ok">✓</span>
          </div>
        ))}
      </div>
    </div>
  </Frame>
);


export { Postkarte, Kompass, VorherNachher, JaStapel, Los };
